<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { ABSENCE_COLOR, ABSENCE_ICON, timelineConflictResolutionEnum } from '@/stores/schedule.store'
import { signalToneColor, signalToneEnum } from '@/composables/dashboard-kpi'

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  formatRange: {
    type: Function,
    required: true,
  },
})

const emit = defineEmits(['resolve', 'cancel'])

const { t } = useI18n()

const { SHIFT_ALLOCATION, DELETE_ALLOCATION, TRIM_ABSENCE, DELETE_ABSENCE } = timelineConflictResolutionEnum
const warningColor = signalToneColor(signalToneEnum.ATTENTION)

const options = computed(() => [
  { value: SHIFT_ALLOCATION, icon: 'mdi-calendar-arrow-right', label: t('common.conflictShiftAllocation'), hint: t('common.conflictShiftAllocationHint') },
  { value: TRIM_ABSENCE, icon: 'mdi-content-cut', label: t('common.conflictTrimAbsence'), hint: t('common.conflictTrimAbsenceHint') },
  { value: DELETE_ALLOCATION, icon: 'mdi-calendar-remove-outline', color: 'negative', label: t('common.conflictDeleteAllocation'), hint: t('common.conflictDeleteAllocationHint') },
  { value: DELETE_ABSENCE, icon: 'mdi-delete-outline', color: 'negative', label: t('common.conflictDeleteAbsence'), hint: t('common.conflictDeleteAbsenceHint') },
])
</script>

<template>
  <q-dialog :model-value="props.modelValue !== null" class="dialog-xxs" @hide="emit('cancel')">
    <q-card v-if="props.modelValue">
      <q-card-section class="row items-center no-wrap q-gutter-sm">
        <q-avatar
          icon="mdi-alert"
          :color="warningColor"
          text-color="white"
          size="md"
        />
        <span class="text-h6">{{ $t('common.availabilityConflict') }}</span>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="q-mb-md">
          {{ $t('common.availabilityConflictMessage', { service: props.modelValue.serviceTitle, project: props.modelValue.projectName }) }}
        </div>

        <div class="conflict-items">
          <div class="conflict-item" :style="{ '--item-color': props.modelValue.color }">
            <q-icon name="mdi-calendar-clock" size="1.125rem" class="conflict-item__icon" />
            <div class="conflict-item__text">
              <div class="text-weight-medium">
                {{ props.modelValue.projectName }}
              </div>
              <div class="conflict-item__caption">
                {{ props.formatRange(props.modelValue.allocation) }}
              </div>
            </div>
          </div>
          <div class="conflict-item" :style="{ '--item-color': ABSENCE_COLOR }">
            <q-icon :name="ABSENCE_ICON" size="1.125rem" class="conflict-item__icon" />
            <div class="conflict-item__text">
              <div class="text-weight-medium">
                {{ $t('common.staff.absence') }}
              </div>
              <div class="conflict-item__caption">
                {{ props.formatRange(props.modelValue.absence) }}
              </div>
            </div>
          </div>
          <div class="conflict-overlap">
            <q-icon name="mdi-alert" size="1rem" :color="warningColor" />
            <span class="text-weight-medium">{{ $t('common.overlap') }}:</span>
            {{ props.formatRange(props.modelValue) }}
          </div>
        </div>

        <q-list class="q-mt-md conflict-options">
          <q-item
            v-for="option in options"
            :key="option.value"
            clickable
            class="conflict-option"
            @click="emit('resolve', props.modelValue, option.value)"
          >
            <q-item-section avatar>
              <q-icon :name="option.icon" :color="option.color ?? 'primary'" />
            </q-item-section>
            <q-item-section>
              <q-item-label :class="option.color ? `text-${option.color}` : undefined">
                {{ option.label }}
              </q-item-label>
              <q-item-label caption>
                {{ option.hint }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat :label="$t('common.cancel')" @click="emit('cancel')" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style scoped lang="scss">
.conflict-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.conflict-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border-left: 0.25rem solid var(--item-color);
  background: color-mix(in srgb, var(--item-color) 8%, transparent);
}

.conflict-item__icon {
  color: var(--item-color);
}

.conflict-item__caption {
  font-size: 0.75rem;
  opacity: 0.7;
}

.conflict-overlap {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--q-warning) 14%, transparent);
  font-size: 0.8125rem;
}

.conflict-options {
  border: 1px solid color-mix(in srgb, var(--q-primary) 15%, transparent);
  border-radius: 0.5rem;
  overflow: hidden;
}

.conflict-option + .conflict-option {
  border-top: 1px solid color-mix(in srgb, var(--q-primary) 8%, transparent);
}
</style>
